import React, { useState, useEffect, useCallback } from "react";
import { Form, InputGroup, Row, Col } from "react-bootstrap"
import { FaCog, FaTarget, FaTimes } from "react-icons/fa"
import "react-datepicker/dist/react-datepicker.css"
import { GetTicketsByID, InsertCoupons, UpdateCouponsData } from "../../../api/Webservices"
import "./Coupon.css"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"

const CreateCouponDrawer = ({ show, onHide, onSave, editMode = false, couponToEdit = null }) => {
  const [couponCode, setCouponCode] = useState("")
  const [discount, setDiscount] = useState("")
  const [expiryDate, setExpiryDate] = useState(null)
  const [expiryTime, setExpiryTime] = useState(null)
  const [enableExpiry, setEnableExpiry] = useState(true)
  const [enableUsageLimits, setEnableUsageLimits] = useState(true)
  const [totalUsageLimit, setTotalUsageLimit] = useState("")
  const [perBuyerLimit, setPerBuyerLimit] = useState("")
  const [activeTab, setActiveTab] = useState("basic")
  const [drawerClass, setDrawerClass] = useState("")
  const [selectedTickets, setSelectedTickets] = useState([])
  const [applyToAllTickets, setApplyToAllTickets] = useState(false)
  const [discountType, setDiscountType] = useState("netAmount")
  const [tickets, setTickets] = useState([])
  const [isLoadingTickets, setIsLoadingTickets] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [hasValue, setHasValue] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [totalUsageClicked, setTotalUsageClicked] = useState(false)
  const [perBuyerClicked, setPerBuyerClicked] = useState(false)
  const EventID = localStorage.getItem("eventId") || 1

  // Validation state
  const [errors, setErrors] = useState({
    couponCode: "",
    discount: "",
    tickets: "",
  })

  // State for notification
  const [notification, setNotification] = useState({
    show: false,
    type: "", // "success" or "error"
    message: "",
  })

  useEffect(() => {
    if (show) {
      setDrawerClass("slide-in")
    } else {
      setDrawerClass("")
    }
  }, [show])

  // Load tickets data
  useEffect(() => {
    if (show) {
      setIsLoadingTickets(true)
      const GetData = {
        event_id: localStorage.getItem("eventId") || "",
        ticket_status: activeTab === "onsale",
      }
      GetTicketsByID(GetData)
        .then((res) => {
          console.log("Tickets data:", res)
          if (res && Array.isArray(res.data?.tickets)) {
            setTickets(res.data.tickets)
          } else {
            setTickets([])
          }
          setIsLoadingTickets(false)
        })
        .catch((err) => {
          console.error("Error fetching tickets:", err)
          setTickets([])
          setIsLoadingTickets(false)
        })
    }
  }, [show])

  // Add this useEffect to handle ticket selection when tickets are loaded
  useEffect(() => {
    console.log("Tickets loaded:", discountType)
    if (editMode && discountType === "tickets" && applyToAllTickets && tickets.length > 0) {
      const allTicketIds = tickets.map((ticket) => ticket.id.toString())
      setSelectedTickets(allTicketIds)
      console.log("All tickets selected:", allTicketIds)
      setDiscountType("tickets")
    }
  }, [tickets, discountType, applyToAllTickets, editMode])

  useEffect(() => {
    if (editMode && couponToEdit) {
      let discountValue = ""
      if (typeof couponToEdit.discount === "string") {
        discountValue = couponToEdit.discount.replace(/[^0-9.]/g, "")
      } else if (couponToEdit._originalData?.discount) {
        discountValue = couponToEdit._originalData.discount
      }

      let parsedExpiryDate = null
      let parsedExpiryTime = null

      if (couponToEdit.expiry_date || couponToEdit._originalData?.expiry_date) {
        const expiryDateStr = couponToEdit.expiry_date || couponToEdit._originalData?.expiry_date
        try {
          const date = new Date(expiryDateStr)
          if (!isNaN(date.getTime())) {
            parsedExpiryDate = date.toISOString().split("T")[0]
          }
        } catch (e) {
          console.error("Error parsing expiry date:", e)
        }
      }

      if (couponToEdit.expiry_time || couponToEdit._originalData?.expiry_time) {
        parsedExpiryTime = couponToEdit.expiry_time || couponToEdit._originalData?.expiry_time
        if (parsedExpiryTime && parsedExpiryTime.includes(":")) {
          parsedExpiryTime = parsedExpiryTime.split(":").slice(0, 2).join(":")
        }
      } else if (parsedExpiryDate && couponToEdit.expiry_date) {
        try {
          const date = new Date(couponToEdit.expiry_date)
          if (!isNaN(date.getTime())) {
            const hours = date.getHours().toString().padStart(2, "0")
            const minutes = date.getMinutes().toString().padStart(2, "0")
            parsedExpiryTime = `${hours}:${minutes}`
          }
        } catch (e) {
          console.error("Error extracting time from expiry date:", e)
        }
      }

      setCouponCode(couponToEdit.coupon_code || couponToEdit._originalData?.coupon_code || "")
      setDiscount(discountValue)
      setExpiryDate(parsedExpiryDate)
      setExpiryTime(parsedExpiryTime)
      setEnableExpiry(!!(parsedExpiryDate || parsedExpiryTime))

      const hasUsageLimits =
        couponToEdit.usage_limit_enabled === "yes" || couponToEdit._originalData?.usage_limit_enabled === "yes"
      setEnableUsageLimits(hasUsageLimits)

      if (hasUsageLimits) {
        const totalLimit = couponToEdit.total_usage_limit || couponToEdit._originalData?.total_usage_limit || ""
        const buyerLimit = couponToEdit.usage_limit_per_buyer || couponToEdit._originalData?.usage_limit_per_buyer || ""

        setTotalUsageLimit(totalLimit)
        setPerBuyerLimit(buyerLimit)

        if (totalLimit && totalLimit !== "∞(unlimited)") {
          setTotalUsageClicked(true)
        }
        if (buyerLimit && buyerLimit !== "∞(unlimited)") {
          setPerBuyerClicked(true)
        }
      }

      const discType =
        couponToEdit.apply_discount_on === "net_amount" ||
          couponToEdit._originalData?.apply_discount_on === "net_amount"
          ? "netAmount"
          : "tickets"
      setDiscountType(discType)

      if (discType === "netAmount") {
        // For net amount, clear ticket selections
        setApplyToAllTickets(false)
        setSelectedTickets([])
      } else {
        // For tickets discount type
        const applyToAll =
          couponToEdit.apply_to_all_tickets === "yes" || couponToEdit._originalData?.apply_to_all_tickets === "yes"

        setApplyToAllTickets(applyToAll)

        if (applyToAll) {
          // If apply to all is enabled, select all tickets
          if (tickets.length > 0) {
            const allTicketIds = tickets.map((ticket) => ticket.id.toString())
            setSelectedTickets(allTicketIds)
          } else {
            // If tickets aren't loaded yet, we'll handle this when tickets are loaded
            // You might want to add a flag to select all tickets once they're loaded
          }
        } else if (couponToEdit.ticket_ids || couponToEdit._originalData?.ticket_ids) {
          // If specific tickets are selected
          let ticketIds = []

          if (Array.isArray(couponToEdit.ticket_ids)) {
            ticketIds = couponToEdit.ticket_ids
          } else if (couponToEdit._originalData?.ticket_ids && Array.isArray(couponToEdit._originalData.ticket_ids)) {
            ticketIds = couponToEdit._originalData.ticket_ids
          } else if (typeof couponToEdit.ticket_ids === "string") {
            try {
              const parsed = JSON.parse(couponToEdit.ticket_ids)
              if (Array.isArray(parsed)) {
                ticketIds = parsed
              }
            } catch (e) {
              console.error("Error parsing ticket_ids:", e)
            }
          }

          setSelectedTickets(ticketIds.map((id) => id.toString()))
        }
      }
    } else {
      resetForm()
    }
  }, [editMode, couponToEdit, show, tickets])

  // Add this at the top of your CreateCouponDrawer component
  const handleToast = (type, message) => {
    // Ensure consistent parameter order
    if (typeof type === "string" && !message) {
      // If only one parameter is provided, assume it's the message and type is success
      showToast(type, "success")
    } else {
      showToast(type, message)
    }
  }

  const handleDraftSave = () => {
    console.log("Draft save clicked")
  }

  // Validate the basic tab fields
  const validateBasicTab = () => {
    let isValid = true
    const newErrors = { ...errors }

    // Validate coupon code
    if (!couponCode.trim()) {
      newErrors.couponCode = "Coupon code is required"
      isValid = false
    } else {
      newErrors.couponCode = ""
    }

    // Validate discount
    if (!discount.trim()) {
      newErrors.discount = "Discount value is required"
      isValid = false
    } else if (isNaN(Number(discount)) || Number(discount) <= 0) {
      newErrors.discount = "Discount must be a positive number"
      isValid = false
    } else if (Number(discount) > 100 && discountType === "tickets") {
      newErrors.discount = "Percentage discount cannot exceed 100%"
      isValid = false
    } else {
      newErrors.discount = ""
    }

    setErrors(newErrors)
    return isValid
  }

  // Validate the apply to tab fields
  const validateApplyToTab = () => {
    let isValid = true
    const newErrors = { ...errors }

    // Validate ticket selection if discount type is tickets and not applying to all tickets
    if (discountType === "tickets" && !applyToAllTickets && selectedTickets.length === 0) {
      newErrors.tickets = "Please select at least one ticket"
      isValid = false
    } else {
      newErrors.tickets = ""
    }

    setErrors(newErrors)
    return isValid
  }

  const handleContinue = () => {
    // If on Basic tab, validate and move to Apply To tab if valid
    if (activeTab === "basic") {
      if (validateBasicTab()) {
        setActiveTab("applyTo")
      }else{
        showToast("Please fill all required fields", "error");
      }
    } else {
      // If on Apply To tab, validate and submit if valid
      if (validateApplyToTab()) {
        handleSubmit()
      }else{
        showToast("Please fill all required fields", "error");
      }
    }
  }

  const handleDiscountTypeChange = (type) => {
    setDiscountType(type)
    console.log("Discount type changed to:", type)

    if (type === "netAmount") {
      // If switching to netAmount, clear ticket selections
      setSelectedTickets([])
      setApplyToAllTickets(false)
    } else if (type === "tickets") {
      // If switching to tickets, automatically select all tickets
      setApplyToAllTickets(true)
      // Select all available tickets
      const allTicketIds = tickets.map((ticket) => ticket.id.toString())
      setSelectedTickets(allTicketIds)
    }

    if (errors.tickets) {
      setErrors({ ...errors, tickets: "" })
    }
  }

  const handleSubmit = (is_draft = false) => {
    // Prevent multiple submissions
    if (isSubmitting) return

    // Validate both tabs before submission
    const isBasicValid = validateBasicTab()
    const isApplyToValid = validateApplyToTab()

    if (!isBasicValid) {
      setActiveTab("basic")
      return
    }

    if (!isApplyToValid) {
      return
    }

    let formattedExpiryDate = null
    let formattedExpiryTime = null

    if (enableExpiry) {
      // For date, just use the string value directly from the date input
      formattedExpiryDate = expiryDate || null

      // For time, use the string value directly from the time input
      formattedExpiryTime = expiryTime || null
    }
    // Format total usage limit and per buyer limit
    let formattedTotalUsageLimit = null
    if (enableUsageLimits && totalUsageLimit && totalUsageLimit !== "") {
      formattedTotalUsageLimit = Number.parseInt(totalUsageLimit, 10)
    }

    let formattedPerBuyerLimit = null
    if (enableUsageLimits && perBuyerLimit && perBuyerLimit !== "") {
      formattedPerBuyerLimit = Number.parseInt(perBuyerLimit, 10)
    }

    // Prepare coupon data for API with the updated structure

    const couponData = {
      event_id: EventID || "",
      coupon_code: couponCode,
      discount: Number.parseInt(discount, 10),
      discount_type: discountType === "netAmount" ? "percentage" : "percentage",
      // apply_discount_on:
      //   discountType === "netAmount" ? "net_amount" : "tickets",
      expiry_date: formattedExpiryDate,
      expiry_time: formattedExpiryTime,
      usage_limit_enabled: enableUsageLimits ? "yes" : "no",
      total_usage_limit: formattedTotalUsageLimit,
      usage_limit_per_buyer: formattedPerBuyerLimit,
      coupon_status: is_draft ? "draft" : "active",
      apply_discount_on: discountType === "netAmount" ? "net_amount" : "tickets",
      apply_to_all_tickets: applyToAllTickets ? "yes" : "no",
      ticket_ids: selectedTickets.length > 0 ? selectedTickets.map((id) => Number.parseInt(id, 10)) : [],
    }
    console.log(couponData, "payload")

    // If in edit mode, include the coupon ID
    if (editMode && couponToEdit) {
      couponData.coupon_id = couponToEdit.id || couponToEdit._originalData?.id
    }

    console.log("Submitting coupon data:", couponData)
    setIsSubmitting(true)

    if (!editMode) {
      // setNotification({
      //   show: true,
      //   type: "info",
      //   message: "Creating coupon...",
      // });

      console.log(couponData)

      InsertCoupons(couponData)
        .then((response) => {
          // Make sure response exists before accessing properties
          if (!response) {
            throw new Error("No response received")
          }

          console.log("Coupon created response:", response)

          if (response.success === true) {
            // Success case
            // showToast("success", "create page Coupon created successfully!");
            onSave(couponData)
            setTimeout(() => {
              onHide()
            }, 2000)
          } else {
            console.error("API error:", response.message)

            if (response.message === "Coupon code already exists for this event") {
              setErrors({
                ...errors,
                couponCode: "This coupon code already exists for this event",
              })
              setActiveTab("basic") // Switch back to basic tab to show the error
            }

            showToast( response.message || "Failed to create coupon","error")
          }
        })
        .catch((error) => {
          // This will catch any JS errors in the then block or network errors
          console.error("Error creating coupon:", error)
          showToast("Failed to create coupon. Please try again." ,"error")
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      // For edit mode, call UpdateCouponsData directly
      // setNotification({
      //   show: true,
      //   type: "info",
      //   message: "Updating coupon...",
      // });

      UpdateCouponsData(couponData)
        .then((response) => {
          console.log("Coupon updated successfully:", response)

          onSave(null, null, true)

          setTimeout(() => {
            onHide()
          }, 2000)
        })
        .catch((error) => {
          console.error("Error updating coupon:", error)

          showToast( "Failed to update coupon. Please try again.","error")
        })
        .finally(() => {
          setIsSubmitting(false)
        })
      console.log("Coupon data to update:", couponData)
    }
  }

  const resetForm = () => {
    setCouponCode("")
    setDiscount("")
    setExpiryDate(null)
    setExpiryTime(null)
    setEnableExpiry(true)
    setEnableUsageLimits(true)
    setTotalUsageLimit("")
    setPerBuyerLimit("")
    setActiveTab("basic")
    setSelectedTickets([])
    setApplyToAllTickets(false)
    setDiscountType("netAmount")
    setTotalUsageClicked(false)
    setPerBuyerClicked(false)
    setErrors({
      couponCode: "",
      discount: "",
      tickets: "",
    })
  }

  const handleTicketSelection = (ticketId) => {
    if (selectedTickets.includes(ticketId)) {
      setSelectedTickets(selectedTickets.filter((id) => id !== ticketId))
    } else {
      setSelectedTickets([...selectedTickets, ticketId])
    }
    // Clear ticket selection error when tickets are selected
    if (errors.tickets) {
      setErrors({ ...errors, tickets: "" })
    }
  }

  const handleApplyToAllTickets = (e) => {
    setApplyToAllTickets(e.target.checked)
    if (e.target.checked) {
      // Select all tickets
      const allTicketIds = tickets.map((ticket) => ticket.id.toString())
      setSelectedTickets(allTicketIds)
      // Clear ticket selection error when "Apply to all tickets" is checked
      if (errors.tickets) {
        setErrors({ ...errors, tickets: "" })
      }
    } else {
      setSelectedTickets([])
    }
  }

  // Close notification
  const closeNotification = () => {
    // setNotification({ ...notification, show: false });
  }

  // Update the total usage limit input field to handle the placeholder text
  const handleTotalUsageChange = (e) => {
    const value = e.target.value
    setTotalUsageLimit(value)
    setHasValue(!!value)
  }

  const handleTotalUsageClick = () => {
    setTotalUsageClicked(true)
    if (totalUsageLimit === "∞(unlimited)") {
      setTotalUsageLimit("")
    }
  }

  const handlePerBuyerClick = () => {
    setPerBuyerClicked(true)
    if (perBuyerLimit === "∞(unlimited)") {
      setPerBuyerLimit("")
    }
  }

  const renderTotalUsageInput = useCallback(() => {
    return (
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder=""
          value={totalUsageClicked ? totalUsageLimit : totalUsageLimit || "∞(unlimited)"}
          onChange={handleTotalUsageChange}
          onClick={handleTotalUsageClick}
          onFocus={() => {
            setIsFocused(true)
            handleTotalUsageClick()
          }}
          onBlur={() => setIsFocused(false)}
        />
      </div>
    )
  }, [totalUsageLimit, totalUsageClicked, isFocused])

  if (!show) return null

  return (
    <>
      <ToastMessage />
      <div
        className={`coupon-drawer-overlay ${show ? "show" : ""}`}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1050,
          display: show ? "block" : "none",
        }}
      >
        <div
          className={`extraspace_search_div1 search_div open ${drawerClass}`}
          style={{
            position: "fixed",
            top: 0,
            right: 0,

            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 15px rgba(0, 0, 0, 0.1)",
            transform: show ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.3s ease-in-out",
            zIndex: 1051,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div className="common_sidebar_header"
          >
            <h5  >
              {editMode ? "Edit Coupon" : "Create Coupon"}
            </h5>
            <button
              onClick={onHide}
              className="close-notification-btn"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="common_sidebar_body"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "24px",
            }}
          >
            {notification.show && (
              <div className={`notification-popup ${notification.type}`}>
                <div className="notification-content">
                  {notification.type === "success" ? (
                    <i className="fa fa-check-circle notification-icon"></i>
                  ) : (
                    <i className="fa fa-exclamation-circle notification-icon"></i>
                  )}
                  <span className="notification-message">{notification.message}</span>
                  <button className="notification-close" onClick={closeNotification}>
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Progress Pills */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "32px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "25px",
                  padding: "4px",
                  border: "1px solid #e9ecef",
                }}
              >
                <div
                  onClick={() => setActiveTab("basic")}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    backgroundColor: activeTab === "basic" ? "#184778" : "transparent",
                    color: activeTab === "basic" ? "white" : "#6c757d",
                  }}
                >
                  {/* <FaCog size={14} /> */}
                  Basic
                </div>
                <div
                  onClick={() => {
                    if (validateBasicTab()) {
                      setActiveTab("applyTo")
                    }else{
                      showToast("Please fill all required fields", "error");
                    }
                  }}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    backgroundColor: activeTab === "applyTo" ? "#184778" : "transparent",
                    color: activeTab === "applyTo" ? "white" : "#6c757d",
                  }}
                >
                  {/* <FaTarget size={14} /> */}
                  Apply to
                </div>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === "basic" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: "600",
                      color: "#184778",
                      marginBottom: "8px",
                      fontSize: "14px",
                    }}
                  >
                    Coupon code <span style={{ color: "#dc3545" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value)
                      if (e.target.value.trim()) {
                        setErrors({ ...errors, couponCode: "" })
                      }
                    }}
                    isInvalid={!!errors.couponCode}
                    style={{
                      borderRadius: "8px",
                      border: "1px solid #e9ecef",
                      padding: "12px 16px",
                      fontSize: "14px",
                    }}
                  />
                  {errors.couponCode && (
                    <Form.Control.Feedback type="invalid">{errors.couponCode}</Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: "600",
                      color: "#184778",
                      marginBottom: "8px",
                      fontSize: "14px",
                    }}
                  >
                    Discount <span style={{ color: "#dc3545" }}>*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      placeholder="Enter percentage"
                      value={discount}
                      onChange={(e) => {
                        setDiscount(e.target.value)
                        if (e.target.value.trim() && !isNaN(Number(e.target.value)) && Number(e.target.value) > 0) {
                          setErrors({ ...errors, discount: "" })
                        }
                      }}
                      isInvalid={!!errors.discount}
                      style={{
                        borderRadius: "8px 0 0 8px",
                        border: "1px solid #e9ecef",
                        padding: "12px 16px",
                        fontSize: "14px",
                      }}
                    />
                    <InputGroup.Text
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                        borderLeft: "none",
                        borderRadius: "0 8px 8px 0",
                        color: "#6c757d",
                        fontSize: "14px",
                      }}
                    >
                      %
                    </InputGroup.Text>
                  </InputGroup>
                  {errors.discount && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.discount}
                    </div>
                  )}
                </Form.Group>

                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "20px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: enableExpiry ? "16px" : "0",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#184778",
                          fontSize: "14px",
                          marginBottom: "4px",
                        }}
                      >
                        Set expiry
                      </div>
                      <div
                        style={{
                          color: "#6c757d",
                          fontSize: "12px",
                          lineHeight: "1.4",
                        }}
                      >
                        Choose a date after which the coupon will be invalid to use
                      </div>
                    </div>
                    <div className="status-toggle-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          className="lv-btn small-switch"
                          checked={enableExpiry}
                          onChange={() => setEnableExpiry(!enableExpiry)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                  </div>

                  {enableExpiry && (
                    <Row>
                      <Col md={6} className="mb-3 mb-md-0">
                        <Form.Label
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "13px",
                            marginBottom: "6px",
                          }}
                        >
                          Date
                        </Form.Label>
                        <input
                          type="date"
                          value={expiryDate || ""}
                          onChange={(e) => setExpiryDate(e.target.value)}
                          className="form-control"
                          min={new Date().toISOString().split("T")[0]}
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "13px",
                            marginBottom: "6px",
                          }}
                        >
                          Time
                        </Form.Label>
                        <input
                          type="time"
                          value={expiryTime || ""}
                          onChange={(e) => setExpiryTime(e.target.value)}
                          className="form-control"
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "20px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: enableUsageLimits ? "16px" : "0",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#184778",
                          fontSize: "14px",
                          marginBottom: "4px",
                        }}
                      >
                        Usage limits
                      </div>
                      <div
                        style={{
                          color: "#6c757d",
                          fontSize: "12px",
                          lineHeight: "1.4",
                        }}
                      >
                        Limit the number of times this coupon can be used
                      </div>
                    </div>
                    <div className="status-toggle-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          className="lv-btn small-switch"
                          checked={enableUsageLimits}
                          onChange={() => setEnableUsageLimits(!enableUsageLimits)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                  </div>

                  {enableUsageLimits && (
                    <Row>
                      <Col xs={12} md={6} className="mb-3 mb-md-0">
                        <Form.Label
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "13px",
                            marginBottom: "6px",
                          }}
                        >
                          Limit total usage
                        </Form.Label>
                        {renderTotalUsageInput()}
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Label
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "13px",
                            marginBottom: "6px",
                          }}
                        >
                          Limit usage per buyer
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={perBuyerClicked ? perBuyerLimit : perBuyerLimit || "∞(unlimited)"}
                          onChange={(e) => {
                            setPerBuyerLimit(e.target.value)
                          }}
                          onClick={handlePerBuyerClick}
                          onFocus={handlePerBuyerClick}
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                </div>
              </div>
            )}

            {activeTab === "applyTo" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "20px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Form.Label
                    style={{
                      fontWeight: "600",
                      color: "#184778",
                      fontSize: "14px",
                      marginBottom: "16px",
                    }}
                  >
                    Apply Discount To
                  </Form.Label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <input
                          type="radio"
                          name="discountType"
                          id="netAmount"
                          checked={discountType === "netAmount"}
                          onChange={() => handleDiscountTypeChange("netAmount")}
                          style={{
                            accentColor: "#184778",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        <label
                          htmlFor="netAmount"
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          Net Amount
                        </label>
                      </div>
                      <div
                        style={{
                          color: "#6c757d",
                          fontSize: "12px",
                          marginLeft: "28px",
                        }}
                      >
                        Discount will be applied to the total order amount
                      </div>
                    </div>

                    {/* <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <input
                          type="radio"
                          name="discountType"
                          id="tickets"
                          checked={discountType === "tickets"}
                          onChange={() => handleDiscountTypeChange("tickets")}
                          style={{
                            accentColor: "#184778",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        <label
                          htmlFor="tickets"
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          Tickets
                        </label>
                      </div>
                      <div
                        style={{
                          color: "#6c757d",
                          fontSize: "12px",
                          marginLeft: "28px",
                        }}
                      >
                        Discount will be applied only to the selected tickets
                      </div>
                    </div> */}
                  </div>
                </div>

                {/* {discountType === "tickets" && (
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "12px",
                      padding: "20px",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <input
                          type="checkbox"
                          id="allTickets"
                          checked={applyToAllTickets}
                          onChange={handleApplyToAllTickets}
                          style={{
                            accentColor: "#184778",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        <label
                          htmlFor="allTickets"
                          style={{
                            fontWeight: "500",
                            color: "#495057",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          Apply to all tickets
                        </label>
                      </div>
                      <span
                        style={{
                          backgroundColor: "#31719c",
                          color: "white",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                      >
                        {selectedTickets.length} tickets selected
                      </span>
                    </div>

                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: errors.tickets ? "1px solid #dc3545" : "1px solid #e9ecef",
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    >
                      {isLoadingTickets ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "24px",
                            color: "#6c757d",
                          }}
                        >
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ marginBottom: "8px" }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p style={{ margin: 0, fontSize: "14px" }}>Loading tickets...</p>
                        </div>
                      ) : !Array.isArray(tickets) || tickets.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "24px",
                            color: "#6c757d",
                            fontSize: "14px",
                          }}
                        >
                          No tickets available
                        </div>
                      ) : (
                        tickets.map((ticket) => (
                          <div
                            key={ticket.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "12px 16px",
                              borderBottom: "1px solid #f8f9fa",
                              cursor: "pointer",
                            }}
                            onClick={() => handleTicketSelection(ticket.id.toString())}
                          >
                            <input
                              type="checkbox"
                              id={`checkbox-permanent-${ticket.id}`}
                              checked={selectedTickets.includes(ticket.id.toString())}
                              onChange={() => handleTicketSelection(ticket.id.toString())}
                              style={{
                                accentColor: "#184778",
                                width: "16px",
                                height: "16px",
                              }}
                            />
                            <label
                              htmlFor={`checkbox-permanent-${ticket.id}`}
                              style={{
                                fontWeight: "500",
                                color: "#495057",
                                fontSize: "14px",
                                cursor: "pointer",
                                flex: 1,
                              }}
                            >
                              {ticket.ticket_name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    {errors.tickets && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <i className="fa fa-exclamation-circle" />
                        {errors.tickets}
                      </div>
                    )}
                  </div>
                )} */}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "20px 24px",
              borderTop: "1px solid #e9ecef",
              backgroundColor: "#f8f9fa",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            {activeTab === "basic" ? (
              <>
                <button
                  onClick={onHide}
                  disabled={isSubmitting}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                    backgroundColor: "white",
                    color: "#6c757d",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleContinue}
                  disabled={isSubmitting}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#184778",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setActiveTab("basic")}
                  disabled={isSubmitting}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                    backgroundColor: "white",
                    color: "#6c757d",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Back
                </button>
                <div style={{ display: "flex", gap: "8px" }}>
                  {/* <button
                    onClick={() => handleSubmit(true)}
                    disabled={isSubmitting}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "8px",
                      border: "1px solid #31719c",
                      backgroundColor: "white",
                      color: "#31719c",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                          style={{ marginRight: "8px" }}
                        ></span>
                        Saving...
                      </>
                    ) : (
                      "Save Draft"
                    )}
                  </button> */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{
                      padding: "10px 24px",
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: "#184778",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                          style={{ marginRight: "8px" }}
                        ></span>
                        {editMode ? "Updating..." : "Creating..."}
                      </>
                    ) : editMode ? (
                      "Update & Activate"
                    ) : (
                      "Create & Activate"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default CreateCouponDrawer
